import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ImageOff, Loader2, PlayCircle, Download, MapPin, X, FileText, Check, Eraser, Star, Quote, Maximize2, Layers, ChevronsLeftRight, Mountain } from "lucide-react";
import { roiRange as rr } from "../lib/roi";
import { toPng } from "html-to-image";
import { GLayoutMap } from "../lib/gmaps";

const ZONE_COLORS = ["#ef4444", "#6366f1", "#16a34a", "#f59e0b", "#0ea5e9"];
const UTIL_LABEL_EN = { water: "Water", gas: "Gas", electric: "Electric", pole: "Power pole", septic: "Septic", storm: "Storm drain", entrance: "Front entry" };
const CARDS_ZH = ["北", "东北", "东", "东南", "南", "西南", "西", "西北"];
const CARDS_EN = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
const cardOf = (deg, zh) => (zh ? CARDS_ZH : CARDS_EN)[Math.round((((deg % 360) + 360) % 360) / 45) % 8];

const SQM_TO_SQFT = 10.7639;
function rectDims(a, b) {
  if (!a || !b) return { wft: 0, hft: 0 };
  const latMid = (a[0] + b[0]) / 2;
  const hM = Math.abs(b[0] - a[0]) * 110540;
  const wM = Math.abs(b[1] - a[1]) * 111320 * Math.cos((latMid * Math.PI) / 180);
  const FT = 3.28084;
  return { wft: Math.round(wM * FT), hft: Math.round(hM * FT) };
}
function polygonAreaSqft(coords) {
  if (!coords || coords.length < 3) return 0;
  const latMid = coords.reduce((s, c) => s + c[0], 0) / coords.length;
  const mLat = 110540, mLng = 111320 * Math.cos((latMid * Math.PI) / 180);
  let area = 0;
  for (let i = 0; i < coords.length; i++) {
    const [y1, x1] = coords[i], [y2, x2] = coords[(i + 1) % coords.length];
    area += (x1 * mLng) * (y2 * mLat) - (x2 * mLng) * (y1 * mLat);
  }
  return Math.round((Math.abs(area) / 2) * SQM_TO_SQFT);
}

function legendSwatch(kind) {
  if (kind === "parcel") return <span className="inline-block w-4 border-t-2 border-dotted" style={{ borderColor: "#ef4444" }} />;
  if (kind === "lot") return <span className="inline-block w-4 h-3 border-2 border-dashed rounded-sm" style={{ borderColor: "#f59e0b" }} />;
  if (kind === "adu") return <span className="inline-block w-4 h-3 border-2 rounded-sm" style={{ borderColor: "#ef4444", background: "rgba(239,68,68,0.25)" }} />;
  if (kind === "marker") return <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#ef4444" }} />;
  if (kind === "line") return <span className="inline-block w-4 border-t-2" style={{ borderColor: "#3b82f6" }} />;
  if (kind === "poly") return <span className="inline-block w-4 h-3 border-2 rounded-sm" style={{ borderColor: "#22c55e", background: "rgba(34,197,94,0.15)" }} />;
  return null;
}

function MapLegend({ zones, annotations, parcel, T }) {
  const zs = zones || [], an = annotations || [];
  const items = [];
  if (parcel && parcel.coords) items.push({ sw: "parcel", label: T("真实地块红线", "Actual parcel line") });
  if (zs.some((z) => z.kind === "lot")) items.push({ sw: "lot", label: T("地块边界(估算)", "Lot boundary (est.)") });
  if (zs.some((z) => z.kind === "adu" || !z.kind)) items.push({ sw: "adu", label: T("ADU 可建区域", "ADU buildable area") });
  if (an.some((a) => a.type === "marker")) items.push({ sw: "marker", label: T("顾问标记点 · 悬停看备注", "Advisor marker · hover for note") });
  if (an.some((a) => a.type === "polyline" || a.type === "freehand")) items.push({ sw: "line", label: T("现场标注线", "On-site markup line") });
  if (an.some((a) => a.type === "polygon")) items.push({ sw: "poly", label: T("标注范围 · 含估算面积", "Marked area · with est. sqft") });
  if (!items.length) return null;
  return (
    <div data-testid="proposal-map-legend" className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5 items-center">
      <span className="text-[11px] font-semibold text-slate-500">{T("图例", "Legend")}:</span>
      {items.map((it, i) => (
        <span key={i} className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">{legendSwatch(it.sw)}{it.label}</span>
      ))}
    </div>
  );
}

function BeforeAfterSlider({ center, zones, annotations, parcel, heading = 0, T, zh }) {
  const [pos, setPos] = useState(50);
  const wrapRef = useRef();
  const move = (clientX) => {
    const r = wrapRef.current.getBoundingClientRect();
    setPos(Math.max(2, Math.min(98, ((clientX - r.left) / r.width) * 100)));
  };
  return (
    <div ref={wrapRef} data-testid="proposal-before-after" className="relative rounded-xl overflow-hidden border border-slate-200 mt-3" style={{ height: 360 }}>
      <div className="absolute inset-0"><GLayoutMap center={center} zones={[]} annotations={[]} parcel={parcel} interactive={false} heading={heading} zh={zh} /></div>
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <GLayoutMap center={center} zones={zones} annotations={annotations} parcel={parcel} interactive={false} heading={heading} zh={zh} />
      </div>
      <div className="absolute inset-0 z-[600] cursor-ew-resize"
        onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); move(e.clientX); }}
        onPointerMove={(e) => { if (e.buttons === 1) move(e.clientX); }}>
        <div className="absolute top-0 bottom-0 w-0.5 bg-white/90 shadow" style={{ left: `${pos}%` }}>
          <div data-testid="proposal-ba-handle" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white shadow flex items-center justify-center">
            <ChevronsLeftRight className="h-4 w-4 text-slate-700" />
          </div>
        </div>
        <span className="absolute top-2 left-2 text-[10px] bg-black/60 text-white rounded px-2 py-0.5">{T("施工前 · 现状", "Before · current")}</span>
        <span className="absolute top-2 right-2 text-[10px] bg-brand-500/85 text-white rounded px-2 py-0.5">{T("规划后 · ADU 布局", "After · ADU plan")}</span>
      </div>
    </div>
  );
}

function LayoutMap({ center, zones, annotations, parcel, zoom = 19, interactive = true, rotate = false, heading = 0, zh }) {
  return <GLayoutMap center={center} zones={zones} annotations={annotations} parcel={parcel} zoom={zoom} interactive={interactive} rotate={rotate} heading={heading} zh={zh} />;
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const money = (n) => "$" + Number(n || 0).toLocaleString();

function SignaturePad({ onChange }) {
  const ref = useRef();
  const drawing = useRef(false);
  const dirty = useRef(false);

  const pos = (e) => {
    const c = ref.current; const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { drawing.current = true; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
  const move = (e) => { if (!drawing.current) return; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.stroke(); dirty.current = true; e.preventDefault(); };
  const end = () => { if (drawing.current && dirty.current) onChange(ref.current.toDataURL("image/png")); drawing.current = false; };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); dirty.current = false; onChange(null); };

  return (
    <div>
      <canvas ref={ref} width={520} height={160} data-testid="signature-pad"
        className="w-full h-40 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <button onClick={clear} data-testid="signature-clear" className="mt-1 text-[11px] text-slate-400 hover:text-red-500 flex items-center gap-1"><Eraser className="h-3 w-3" />清除重签 Clear</button>
    </div>
  );
}

function AcceptModal({ estimate, onClose, onConfirm, submitting }) {
  const [agreed, setAgreed] = useState(false);
  const [sig, setSig] = useState(null);
  const [note, setNote] = useState("");
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3" onClick={onClose} data-testid="accept-modal">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[92vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-slate-800">确认接受报价 · Confirm</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-red-500"><X className="h-5 w-5" /></button>
        </div>
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 mb-4 text-sm text-slate-700">
          {estimate?.packageName} · {estimate?.sf}sf · <span className="font-bold text-red-600">{money(estimate?.total)}</span>
        </div>
        <label className="flex items-start gap-2 text-[12px] text-slate-600 mb-4 cursor-pointer" data-testid="accept-terms">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5" />
          <span>我已阅读并同意本报价内容。此报价为初步估算，最终价格以现场勘测、设计图纸及正式合同为准，有效期 30 天。I have read and agree to this quote.</span>
        </label>
        <p className="text-[12px] font-medium text-slate-500 mb-1">业主签名 · Signature</p>
        <SignaturePad onChange={setSig} />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="留言(选填) Note" data-testid="accept-note"
          className="mt-3 w-full text-sm rounded-lg border border-slate-200 px-3 py-2" />
        <button disabled={!agreed || !sig || submitting} data-testid="accept-confirm-btn" onClick={() => onConfirm(estimate?.id, sig, note)}
          className="mt-4 w-full rounded-xl bg-brand-500 hover:bg-brand-600 text-white py-3 font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}确认接受 · Confirm Accept
        </button>
        {(!agreed || !sig) && <p className="text-[11px] text-slate-400 text-center mt-2">请勾选条款并完成签名 · Agree & sign to continue</p>}
      </div>
    </div>
  );
}

function TestimonialsCarousel({ items, T }) {
  const [idx, setIdx] = useState(0);
  const n = items.length;
  useEffect(() => {
    if (n <= 1) return;
    const t = setInterval(() => setIdx((x) => (x + 1) % n), 5000);
    return () => clearInterval(t);
  }, [n]);
  const tm = items[Math.min(idx, n - 1)];
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4" data-testid="proposal-testimonials">
      <p className="text-[11px] text-slate-400 mb-3">{T("往期业主评价", "What past clients say")}</p>
      <div key={idx} data-testid={`testimonial-${idx}`} className="rounded-xl bg-slate-50 border border-slate-100 p-4 min-h-[110px] animate-in fade-in duration-500 relative">
        {tm.featured && <span className="absolute top-2 right-2 text-[10px] bg-red-100 text-red-600 rounded-full px-2 py-0.5 font-medium" data-testid="testimonial-featured-badge">{T("精选", "Featured")}</span>}
        <Quote className="h-4 w-4 text-red-300 mb-1" />
        <p className="text-[14px] text-slate-700 leading-relaxed">{tm.text}</p>
        <div className="flex items-center gap-1 mt-2">
          {Array.from({ length: 5 }).map((_, s) => (
            <Star key={s} className={`h-3.5 w-3.5 ${s < (tm.stars || 5) ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
          ))}
          <span className="ml-1.5 text-[11px] text-slate-500 truncate">{[tm.clientName, tm.project].filter(Boolean).join(" · ")}</span>
        </div>
      </div>
      {n > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-3" data-testid="testimonial-dots">
          {items.map((_, i) => (
            <button key={i} data-testid={`testimonial-dot-${i}`} onClick={() => setIdx(i)}
              className={`h-2 rounded-full transition-all ${i === idx ? "w-5 bg-red-500" : "w-2 bg-slate-200 hover:bg-slate-300"}`} />
          ))}
        </div>
      )}
    </div>
  );
}

function BookVisitModal({ clientName, onClose, onConfirm, submitting, T }) {
  const [name, setName] = useState(clientName || "");
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState("");
  const [timeSlot, setTimeSlot] = useState("上午 Morning");
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const submit = () => {
    if (!phone.trim()) { setErr(T("请填写联系电话", "Please enter your phone")); return; }
    onConfirm({ name, phone, date, timeSlot, note });
  };
  return (
    <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4" data-testid="book-visit-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-slate-800 flex items-center gap-2"><MapPin className="h-4 w-4 text-red-500" />{T("预约现场勘测", "Book a Site Visit")}</h3>
          <button onClick={onClose} data-testid="book-visit-close" className="text-slate-400 hover:text-red-600"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-[12px] text-slate-500 mb-3">{T("留下方便的时间，我们的顾问会尽快上门为您评估 ADU 可建方案。", "Leave a preferred time — our advisor will visit to assess your ADU options.")}</p>
        <div className="space-y-2.5">
          <input data-testid="book-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={T("称呼 Name", "Name")} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <input data-testid="book-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={T("联系电话 Phone *", "Phone *")} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <input data-testid="book-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <select data-testid="book-timeslot" value={timeSlot} onChange={(e) => setTimeSlot(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <option>上午 Morning</option><option>下午 Afternoon</option><option>晚上 Evening</option>
            </select>
          </div>
          <textarea data-testid="book-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={T("备注(可选)", "Note (optional)")} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          {err && <p className="text-[12px] text-red-500">{err}</p>}
        </div>
        <button data-testid="book-visit-submit" onClick={submit} disabled={submitting}
          className="mt-4 w-full rounded-xl bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-semibold py-2.5 flex items-center justify-center gap-2">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}{T("提交预约", "Submit")}
        </button>
      </div>
    </div>
  );
}

export default function ProposalShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [acceptFor, setAcceptFor] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lang, setLang] = useState("zh");
  const [rvStars, setRvStars] = useState(5);
  const [rvText, setRvText] = useState("");
  const [rvSubmitting, setRvSubmitting] = useState(false);
  const [rvResult, setRvResult] = useState(null);
  const [layoutIdx, setLayoutIdx] = useState(0);
  const [mapFull, setMapFull] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [baOpen, setBaOpen] = useState(false);
  const [booked, setBooked] = useState(false);
  const [bookedInfo, setBookedInfo] = useState(null);
  const [booking, setBooking] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const fullMapRef = useRef(null);
  const zh = lang === "zh";
  const T = (z, e) => (zh ? z : e);

  const load = () => fetch(`${BACKEND}/api/public/proposal/${token}`)
    .then((r) => { if (!r.ok) throw new Error("链接无效或已失效 / Invalid link"); return r.json(); })
    .then(setData)
    .catch((e) => setErr(e.message));
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const fileUrl = (fid) => `${BACKEND}/api/public/proposal/${token}/file/${fid}`;

  const submitReview = async () => {
    if (!rvText.trim()) return;
    setRvSubmitting(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/proposal/${token}/review`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: rvText, stars: rvStars }) });
      if (!r.ok) throw new Error();
      const j = await r.json();
      setRvResult(j);
      await load();
    } catch { /* ignore */ }
    setRvSubmitting(false);
  };

  const confirmAccept = async (estimateId, signature, note) => {
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/proposal/${token}/accept`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ estimateId, signature, agreedTerms: true, note }) });
      if (!r.ok) throw new Error();
      setAcceptFor(null);
      await load();
    } catch { /* ignore */ }
    setSubmitting(false);
  };

  const submitBooking = async (form) => {
    setBooking(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/proposal/${token}/book-visit`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (!r.ok) throw new Error();
      setBookedInfo(form); setBooked(true); setBookOpen(false);
    } catch { /* ignore */ }
    setBooking(false);
  };

  const selectLayoutPlan = async (planId) => {
    try {
      const r = await fetch(`${BACKEND}/api/public/proposal/${token}/select-plan`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ planId }) });
      if (!r.ok) throw new Error();
      await load();
    } catch { /* ignore */ }
  };

  const [annotPlan, setAnnotPlan] = useState(null);
  const [annotPin, setAnnotPin] = useState(null); // {x,y} in 0..1
  const [annotText, setAnnotText] = useState("");
  const [annotBusy, setAnnotBusy] = useState(false);
  const submitAnnot = async () => {
    if (!annotPlan || !annotText.trim()) return;
    setAnnotBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/proposal/${token}/plan-note`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planId: annotPlan.id, x: annotPin?.x ?? 0.5, y: annotPin?.y ?? 0.5, text: annotText.trim() }) });
      if (!r.ok) throw new Error();
      setAnnotPlan(null); setAnnotPin(null); setAnnotText("");
      await load();
    } catch { /* ignore */ }
    setAnnotBusy(false);
  };

  const downloadMap = async (name) => {
    const node = fullMapRef.current;
    if (!node) return;
    setCapturing(true);
    try {
      await new Promise((r) => setTimeout(r, 300));
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        filter: (el) => !(el.classList && (el.classList.contains("leaflet-control-container") || el.classList.contains("leaflet-control"))),
      });
      const a = document.createElement("a");
      a.download = `ADU选址方案_${name || "site"}.png`;
      a.href = dataUrl;
      a.click();
    } catch (e) {
      alert(T("图片导出失败，请重试或直接截屏保存。", "Export failed — please try again or take a screenshot."));
    }
    setCapturing(false);
  };

  if (err) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-center p-8" data-testid="proposal-share-error">
      <ImageOff className="h-12 w-12 text-slate-300 mb-4" /><p className="text-slate-500">{err}</p>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 text-brand-500 animate-spin" /></div>;

  const estimates = data.estimates || [];
  const layoutPlans = data.layoutPlans || [];
  const empty = (data.images || []).length === 0 && (data.links || []).length === 0 && (data.files || []).length === 0 && estimates.length === 0 && layoutPlans.length === 0;
  const accepted = data.accepted;
  const multi = estimates.length > 1;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="proposal-share-page">
      <header className="bg-slate-900 text-white px-5 py-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><span className="text-lg font-bold tracking-wide text-red-500">ALIGCUS</span><span className="text-[11px] text-slate-300 uppercase tracking-widest">Architectural Construction</span></div>
            <button data-testid="proposal-lang-toggle" onClick={() => setLang(zh ? "en" : "zh")} className="text-[12px] rounded-full border border-slate-600 px-3 py-1 text-slate-200 hover:bg-slate-800">{zh ? "EN" : "中文"}</button>
          </div>
          <h1 className="mt-3 text-2xl font-bold" data-keep-title>{data.clientName || T("您的方案", "Your Proposal")}</h1>
          {data.address && <p className="mt-1 text-sm text-slate-300 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{data.address}</p>}
          <button data-testid="proposal-book-visit-header" onClick={() => setBookOpen(true)}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold px-5 py-2.5 transition-colors">
            <MapPin className="h-4 w-4" />{T("预约现场勘测", "Book a Site Visit")}
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-6 space-y-8">
        {data.coverFileId && (
          <div data-testid="proposal-cover" className="rounded-2xl overflow-hidden border border-slate-200">
            <img src={fileUrl(data.coverFileId)} alt="" className="w-full max-h-72 object-cover" />
          </div>
        )}
        {data.advisor && (
          <div data-testid="proposal-advisor" className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 p-4">
            {data.advisor.avatar
              ? <img src={data.advisor.avatar} alt="" className="h-12 w-12 rounded-full object-cover border border-slate-100" />
              : <div className="h-12 w-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold text-lg">{(data.advisor.name || "A").slice(0, 1)}</div>}
            <div className="min-w-0">
              <p className="text-[11px] text-slate-400">{T("您的专属顾问", "Your Advisor")}</p>
              <p className="font-semibold text-slate-800 truncate">{data.advisor.name}{data.advisor.phone ? ` · ${data.advisor.phone}` : ""}</p>
            </div>
            {data.advisor.wechatQr ? (
              <div className="ml-auto flex flex-col items-center gap-1" data-testid="proposal-advisor-wechat">
                <img src={data.advisor.wechatQr} alt="WeChat QR" className="h-16 w-16 rounded-lg object-cover border border-slate-200" />
                <span className="text-[10px] text-emerald-600 font-medium whitespace-nowrap">{T("扫码加顾问微信", "Scan to add on WeChat")}</span>
              </div>
            ) : (
              <p className="ml-auto text-[12px] text-slate-400 hidden sm:block">{T("欢迎查看您的专属 ADU 方案 👋", "Welcome! Your personalized ADU proposal 👋")}</p>
            )}
          </div>
        )}
        {data.advisor && (data.advisor.testimonials || []).length > 0 && (
          <TestimonialsCarousel items={data.advisor.testimonials} T={T} />
        )}
        {(data.siteLayouts || []).length > 0 && (() => {
          const layouts = data.siteLayouts;
          const L = layouts[Math.min(layoutIdx, layouts.length - 1)];
          const allc = (L.zones || []).flatMap((z) => z.coords || []);
          const center = (data.lat && data.lng) ? [Number(data.lat), Number(data.lng)]
            : allc.length ? [allc.reduce((s, c) => s + c[0], 0) / allc.length, allc.reduce((s, c) => s + c[1], 0) / allc.length] : null;
          if (!center) return null;
          return (
            <section data-testid="proposal-site-layout" className="rounded-2xl bg-white border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-slate-800 flex items-center gap-2"><MapPin className="h-4 w-4 text-red-500" />{T("ADU 选址方案", "ADU Site Plan")}</h2>
                {layouts.length > 1 && (
                  <div className="flex items-center gap-2">
                    <button data-testid="proposal-compare-btn" onClick={() => setCompareOpen(true)}
                      className="text-xs rounded-lg bg-slate-900 text-white px-3 py-1.5 hover:bg-slate-800 flex items-center gap-1">
                      <Layers className="h-3.5 w-3.5" />{T(`并排对比 (${layouts.length})`, `Compare (${layouts.length})`)}
                    </button>
                    <select data-testid="proposal-layout-select" value={layoutIdx} onChange={(e) => setLayoutIdx(Number(e.target.value))} className="text-xs rounded-lg border border-slate-200 px-2 py-1">
                      {layouts.map((x, i) => <option key={i} value={i}>{x.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div className="relative rounded-xl overflow-hidden border border-slate-200 group" style={{ height: 360 }}>
                <LayoutMap key={layoutIdx} center={center} zones={L.zones} annotations={L.annotations} parcel={data.parcel} heading={L.heading || 0} rotate zh={zh} />
                <button data-testid="proposal-map-fullscreen-btn" onClick={() => setMapFull(true)}
                  className="absolute bottom-3 right-3 z-[500] flex items-center gap-1.5 rounded-full bg-slate-900/85 backdrop-blur px-4 py-2 text-xs font-semibold text-white shadow-lg hover:bg-slate-900 transition-colors">
                  <Maximize2 className="h-3.5 w-3.5" />{T("点击看大图", "View full map")}
                </button>
              </div>
              <MapLegend zones={L.zones} annotations={L.annotations} parcel={data.parcel} T={T} />
              {data.parcel && data.parcel.lotSqft && (
                <p className="text-[11px] text-slate-500 mt-1" data-testid="proposal-parcel-calibration">{T("真实地块面积约", "Lot area ~")} {Number(data.parcel.lotSqft).toLocaleString()} sf{data.parcel.aduMaxSf ? ` · ${T("建议 ADU 可建上限约", "suggested ADU max ~")} ${Number(data.parcel.aduMaxSf).toLocaleString()} sf` : ""}</p>
              )}
              {L.note && <p className="text-xs text-slate-500 mt-2">📝 {L.note}</p>}
              <p className="text-[11px] text-slate-400 mt-1">{T("红框为 ADU 大致可建位置与面积，最终以现场勘测与设计图纸为准。", "Marked area shows the approximate ADU location & size; final subject to site survey and drawings.")}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button data-testid="proposal-book-visit-btn" onClick={() => setBookOpen(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold px-5 py-2.5 transition-colors">
                  <MapPin className="h-4 w-4" />{T("预约现场勘测", "Book a Site Visit")}
                </button>
                <button data-testid="proposal-ba-toggle" onClick={() => setBaOpen((v) => !v)}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-semibold px-5 py-2.5 transition-colors">
                  <ChevronsLeftRight className="h-4 w-4" />{baOpen ? T("收起前后对比", "Hide before/after") : T("施工前后对比", "Before / After")}
                </button>
              </div>
              {baOpen && <BeforeAfterSlider center={center} zones={L.zones} annotations={L.annotations} parcel={data.parcel} heading={L.heading || 0} T={T} zh={zh} />}

              {mapFull && (
                <div data-testid="proposal-map-fullscreen" className="fixed inset-0 z-[9999] bg-slate-950 flex flex-col">
                  <div className="mobile-safe-topbar flex items-center justify-between px-4 py-3 bg-slate-900 text-white">
                    <div className="flex items-center gap-2 min-w-0">
                      <MapPin className="h-4 w-4 text-red-500 shrink-0" />
                      <span className="font-semibold truncate">{L.name || T("ADU 选址方案", "ADU Site Plan")}</span>
                      {layouts.length > 1 && (
                        <select data-testid="proposal-fullscreen-layout-select" value={layoutIdx} onChange={(e) => setLayoutIdx(Number(e.target.value))} className="ml-2 text-xs rounded-lg bg-slate-800 border border-slate-700 px-2 py-1 text-white">
                          {layouts.map((x, i) => <option key={i} value={i}>{x.name}</option>)}
                        </select>
                      )}
                    </div>
                    <button data-testid="proposal-map-fullscreen-close" onClick={() => setMapFull(false)} className="flex items-center gap-1 rounded-full bg-slate-800 hover:bg-slate-700 px-3 py-1.5 text-sm"><X className="h-4 w-4" />{T("关闭", "Close")}</button>
                  </div>
                  <div className="flex-1 relative" ref={fullMapRef}>
                    <LayoutMap key={`full-${layoutIdx}`} center={center} zones={L.zones} annotations={L.annotations} parcel={data.parcel} heading={L.heading || 0} rotate zh={zh} />
                    <button data-testid="proposal-map-download" onClick={() => downloadMap(L.name)} disabled={capturing}
                      className="absolute bottom-3 right-3 z-[500] flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 px-4 py-2 text-xs font-semibold text-white shadow-lg transition-colors">
                      {capturing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}{T("下载图片", "Download image")}
                    </button>
                  </div>
                  <div className="px-4 py-2 bg-slate-900 flex items-center justify-center gap-3 flex-wrap">
                    <p className="text-[11px] text-slate-400 text-center">{T("红框为 ADU 大致可建位置与面积，最终以现场勘测与设计图纸为准。", "Marked area shows the approximate ADU location & size; final subject to site survey and drawings.")}</p>
                    <button data-testid="proposal-book-visit-fullscreen" onClick={() => { setMapFull(false); setBookOpen(true); }}
                      className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-xs font-semibold px-4 py-1.5">
                      <MapPin className="h-3.5 w-3.5" />{T("预约现场勘测", "Book a Site Visit")}
                    </button>
                  </div>
                </div>
              )}
            </section>
          );
        })()}
        {layoutPlans.length > 0 && (
          <section data-testid="proposal-layout-plans" className="rounded-2xl bg-white border border-slate-200 p-4">
            <h2 className="font-bold text-slate-800 flex items-center gap-2 mb-1"><Layers className="h-4 w-4 text-red-500" />{T("ADU 布局方案", "ADU Layout Plans")}{layoutPlans.length > 1 ? ` · ${T("并排对比", "compare")}` : ""}</h2>
            <p className="text-[12px] text-slate-500 mb-3">{layoutPlans.length > 1 ? T("我们为您准备了多个 ADU 布局方案，点击「选择此方案」告诉我们您最中意的一个。", "We prepared several ADU layout options — tap “Choose” to tell us your favorite.") : T("以下为您的 ADU 布局方案示意。", "Your ADU layout plan below.")}</p>
            {data.siteConditions && (() => {
              const sc = data.siteConditions;
              const lines = [];
              if (sc.minFt !== null && sc.minFt !== undefined) {
                lines.push({ k: "elev", tone: "slate", txt: T(`场地高程 ${sc.minFt}–${sc.maxFt} ft · 等高线间距 ${sc.intervalFt} ft（${sc.source || "USGS"}）`, `Elevation ${sc.minFt}–${sc.maxFt} ft · contour interval ${sc.intervalFt} ft (${sc.source || "USGS"})`) });
              }
              if (sc.flow && sc.flow.bearingDeg !== null && sc.flow.bearingDeg !== undefined) {
                lines.push({ k: "flow", tone: "sky", txt: T(`坡向/排水：向${cardOf(sc.flow.bearingDeg, true)}下降 · 平均坡度约 ${sc.flow.pct}%（初步判断，工程排水以土建设计为准）`, `Drainage: descending ${cardOf(sc.flow.bearingDeg, false)} · ~${sc.flow.pct}% avg grade (preliminary)`) });
              } else if (sc.slope && sc.slope.dropFt !== null && sc.slope.dropFt !== undefined) {
                lines.push({ k: "slope", tone: "slate", txt: T(`地块内高差约 ${sc.slope.dropFt} ft（坡度约 ${sc.slope.pct}%）`, `~${sc.slope.dropFt} ft drop across lot (~${sc.slope.pct}%)`) });
              }
              if (!lines.length) return null;
              return (
                <div data-testid="proposal-site-conditions" className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 mb-3 space-y-1">
                  <p className="text-[12px] font-bold text-slate-700 flex items-center gap-1.5"><Mountain className="h-3.5 w-3.5 text-amber-600" />{T("场地条件小结", "Site Conditions Summary")}</p>
                  {lines.map((l) => (
                    <p key={l.k} className={`text-[11px] ${l.tone === "sky" ? "text-sky-700" : "text-slate-600"}`} data-testid={`proposal-site-cond-${l.k}`}>{l.txt}</p>
                  ))}
                </div>
              );
            })()}
            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(layoutPlans.length, 3)}, minmax(0, 1fr))` }}>
              {layoutPlans.map((p) => {
                const sel = data.selectedLayoutPlanId === p.id;
                return (
                  <div key={p.id} data-testid={`proposal-plan-${p.id}`} className={`rounded-xl overflow-hidden border-2 flex flex-col ${sel ? "border-red-500 ring-2 ring-red-100" : "border-slate-200"}`}>
                    <button onClick={() => setLightbox(fileUrl(p.renderFileId || p.fileId))} data-testid={`proposal-plan-img-${p.id}`} className="block bg-slate-100 relative w-full">
                      <img src={fileUrl(p.renderFileId || p.fileId)} alt={p.name} className="w-full h-52 object-cover" loading="lazy" />
                      {p.renderFileId && <span className="absolute top-2 left-2 text-[10px] bg-emerald-600 text-white px-2 py-0.5 rounded-full font-semibold">{T("AI 效果图", "AI render")}</span>}
                    </button>
                    <div className="p-2.5 flex flex-col gap-1.5 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-slate-800 text-sm truncate">{p.name}</span>
                        {p.aduSf ? <span className="text-[11px] text-red-600 font-medium shrink-0">{Number(p.aduSf).toLocaleString()} sf{p.is3d ? " ≈" : ""}</span> : null}
                      </div>
                      {p.aduSf && p.perSf ? <p className="text-[12px] text-slate-700 font-semibold" data-testid={`proposal-plan-price-${p.id}`}>{T("预估造价约", "Est. price ~")} ${Number(Math.round(p.aduSf * p.perSf)).toLocaleString()}</p> : null}
                      {p.siteCheck && (["overlap", "near", "clear"].includes(p.siteCheck.building) || ["out", "in"].includes(p.siteCheck.setback) || p.siteCheck.treeFt != null) && (
                        <div className="flex flex-wrap gap-1" data-testid={`proposal-plan-sitecheck-${p.id}`}>
                          {p.siteCheck.building === "overlap" && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-red-50 text-red-600 border border-red-200">{T("与现有建筑重叠", "Overlaps building")}</span>}
                          {p.siteCheck.building === "near" && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-red-50 text-red-600 border border-red-200">{T(`距建筑约 ${p.siteCheck.nearDistFt} ft`, `~${p.siteCheck.nearDistFt} ft to building`)}</span>}
                          {p.siteCheck.building === "clear" && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">{T("建筑避让通过", "Building clearance OK")}</span>}
                          {p.siteCheck.setback === "out" && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-red-50 text-red-600 border border-red-200">{T("超出退界参考区", "Outside setback guide")}</span>}
                          {p.siteCheck.setback === "in" && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">{T("退界界内", "Inside setback guide")}</span>}
                          {p.siteCheck.treeFt != null && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-amber-50 text-amber-700 border border-amber-200">{T(`距树木约 ${p.siteCheck.treeFt} ft`, `~${p.siteCheck.treeFt} ft to tree`)}</span>}
                        </div>
                      )}
                      {Array.isArray(p.utilEst) && p.utilEst.length > 0 && (
                        <div className="rounded-lg bg-sky-50 border border-sky-100 px-2 py-1.5 space-y-0.5" data-testid={`proposal-plan-utilest-${p.id}`}>
                          <p className="text-[10px] font-bold text-sky-800">{T("接管线/步道费用初步估算", "Utility hookup / walkway estimate")}</p>
                          {p.utilEst.map((u, ui) => (
                            <p key={ui} className="text-[10px] text-slate-600">
                              {T(u.label, UTIL_LABEL_EN[u.icon] || u.label)} ≈{u.distFt} ft · <b className="text-sky-800">${Number(u.low).toLocaleString()}–${Number(u.high).toLocaleString()}</b>
                            </p>
                          ))}
                          <p className="text-[10px] font-bold text-sky-900">
                            {T("合计约", "Total ~")} ${p.utilEst.reduce((s, u) => s + (u.low || 0), 0).toLocaleString()}–${p.utilEst.reduce((s, u) => s + (u.high || 0), 0).toLocaleString()}
                          </p>
                          <p className="text-[9px] text-slate-400">{T("按经验单价初步估算，最终以现场勘测与市政要求为准", "Rough estimate; final subject to site survey & utility requirements")}</p>
                        </div>
                      )}
                      {p.note && <p className="text-[11px] text-slate-500 line-clamp-2">📝 {p.note}</p>}
                      {(p.ownerNotes || []).length > 0 && <p className="text-[11px] text-amber-600" data-testid={`proposal-plan-notecount-${p.id}`}>{T(`您已标注 ${p.ownerNotes.length} 条建议`, `${p.ownerNotes.length} note(s) added`)}</p>}
                      <div className="flex items-center gap-2 mt-1">
                        <a data-testid={`proposal-plan-pdf-${p.id}`} href={`${BACKEND}/api/public/proposal/${token}/plan/${p.id}/pdf`} target="_blank" rel="noreferrer" className="text-[12px] text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"><Download className="h-3.5 w-3.5" />{T("方案书 PDF", "Plan PDF")}</a>
                        <button data-testid={`proposal-plan-annot-${p.id}`} onClick={() => setAnnotPlan(p)} className="text-[12px] text-amber-600 hover:text-amber-800 inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{T("标注建议", "Add a note")}</button>
                      </div>
                      <button data-testid={`proposal-plan-select-${p.id}`} onClick={() => selectLayoutPlan(p.id)} disabled={sel}
                        className={`mt-auto w-full rounded-lg py-2 text-sm font-semibold transition ${sel ? "bg-emerald-600 text-white" : "bg-slate-900 text-white hover:bg-slate-800"}`}>
                        {sel ? <span className="inline-flex items-center gap-1"><Check className="h-4 w-4" />{T("已选择", "Selected")}</span> : T("选择此方案", "Choose this plan")}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">{T("图中标注面积为大致估算，最终以现场勘测与设计图纸为准。", "Marked areas are approximate; final subject to site survey and drawings.")}</p>
          </section>
        )}
        {empty && <p className="text-center text-slate-400 py-12">{T("暂无方案内容", "No proposal content yet")}</p>}

        {accepted && (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 text-center" data-testid="proposal-accepted">
            <p className="text-emerald-700 font-semibold text-lg">{T("✓ 您已接受方案", "✓ Proposal Accepted")}</p>
            <p className="text-[12px] text-emerald-600 mt-1">{T("顾问会尽快与您联系推进签约。", "Our advisor will contact you shortly.")}</p>
            {data.confirmationFileId && <a href={fileUrl(data.confirmationFileId)} target="_blank" rel="noreferrer" data-testid="proposal-confirmation-pdf" className="inline-flex items-center gap-1.5 text-sm text-emerald-700 underline mt-2"><FileText className="h-4 w-4" />{T("下载接受确认单", "Download Confirmation")}</a>}
          </div>
        )}

        {accepted && (
          data.reviewSubmitted ? (
            <div className="rounded-2xl bg-white border border-slate-200 p-5 text-center" data-testid="proposal-review-thanks">
              <div className="flex items-center justify-center gap-1 mb-1">
                {Array.from({ length: 5 }).map((_, s) => <Star key={s} className="h-4 w-4 text-amber-400 fill-amber-400" />)}
              </div>
              <p className="text-slate-700 font-medium">{T("感谢您的评价！", "Thank you for your review!")}</p>
              {rvResult && rvResult.promote && (
                <div className="mt-3" data-testid="review-promote">
                  <p className="text-[12px] text-slate-500 mb-2">{T("如果您愿意，欢迎在以下平台分享您的好评 🙏", "Please consider sharing your review 🙏")}</p>
                  <div className="flex items-center justify-center gap-2">
                    {rvResult.googleUrl && <a data-testid="review-google-link" href={rvResult.googleUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-sm px-4 py-2 hover:bg-slate-800">Google</a>}
                    {rvResult.yelpUrl && <a data-testid="review-yelp-link" href={rvResult.yelpUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 text-white text-sm px-4 py-2 hover:bg-brand-600">Yelp</a>}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="proposal-review-form">
              <p className="text-sm font-semibold text-slate-800">{T("为您的顾问留下评价", "Rate your advisor")}</p>
              <p className="text-[12px] text-slate-400 mt-0.5 mb-3">{T("您的评价将帮助更多业主了解我们的服务。", "Your review helps other owners.")}</p>
              <div className="flex items-center gap-1 mb-3" data-testid="review-stars">
                {Array.from({ length: 5 }).map((_, s) => (
                  <button key={s} data-testid={`review-star-${s + 1}`} onClick={() => setRvStars(s + 1)}>
                    <Star className={`h-6 w-6 ${s < rvStars ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
                  </button>
                ))}
              </div>
              <textarea data-testid="review-text" value={rvText} onChange={(e) => setRvText(e.target.value)} rows={3}
                placeholder={T("说说您的体验…", "Tell us about your experience…")}
                className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:border-brand-400 focus:outline-none resize-none" />
              <button disabled={!rvText.trim() || rvSubmitting} data-testid="review-submit-btn" onClick={submitReview}
                className="mt-3 w-full rounded-xl bg-brand-500 hover:bg-brand-600 text-white py-2.5 font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                {rvSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}{T("提交评价", "Submit Review")}
              </button>
            </div>
          )
        )}

        {estimates.length > 0 && (
          <section data-testid="proposal-estimates">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{multi ? T("报价方案对比", "Compare Options") : T("报价", "Estimate")}</h2>
            <div className={`grid gap-4 ${multi ? "md:grid-cols-2" : "grid-cols-1"}`}>
              {estimates.map((e) => {
                const isAccepted = data.acceptedEstimateId === e.id;
                return (
                  <div key={e.id} data-testid={`proposal-estimate-${e.id}`} className={`rounded-2xl bg-white border overflow-hidden ${isAccepted ? "border-emerald-400 ring-2 ring-emerald-200" : "border-slate-200"}`}>
                    <div className="bg-slate-900 text-white px-4 py-3 flex items-baseline justify-between">
                      <div><p className="text-[10px] text-slate-300">{T("报价总额", "Total")}</p><p className="text-2xl font-bold text-red-500" data-testid={`proposal-total-${e.id}`}>{money(e.total)}</p></div>
                      <div className="text-right text-[11px] text-slate-300">{e.packageName}<br />{e.sf} sf · {e.layout}</div>
                    </div>
                    <div className="p-4 space-y-2">
                      {(e.lineItems || []).map((li, i) => (
                        <div key={i} className="flex justify-between text-[13px] text-slate-600 border-b border-slate-50 pb-1"><span className="truncate pr-2">{li.name}</span><span className="font-medium text-slate-800">{money(li.amount)}</span></div>
                      ))}
                      {e.roi && (
                        <div className="grid grid-cols-2 gap-2 pt-1">
                          {[[T("月租金", "Rent/mo"), rr(e.roi, "monthlyRentLow", "monthlyRentHigh", "monthlyRent", money)], [T("回本", "Payback"), rr(e.roi, "paybackLow", "paybackHigh", "paybackYears", (v) => v, T("年", "y"))], [T("毛收益率", "Yield"), rr(e.roi, "grossYieldLow", "grossYieldHigh", "grossYield", (v) => `${v}%`)], [T("净增值", "Equity+"), e.roi.netEquityGain && money(e.roi.netEquityGain)]].map((m, i) => (
                            <div key={i} className="rounded-lg bg-slate-50 py-1.5 text-center"><p className="text-[10px] text-slate-400">{m[0]}</p><p className="text-[13px] font-semibold text-slate-800">{m[1] || "-"}</p></div>
                          ))}
                        </div>
                      )}
                      {e.aiRoiText && <p className="text-[12px] text-slate-600 leading-relaxed whitespace-pre-line pt-1 border-t border-slate-100 line-clamp-6">{e.aiRoiText}</p>}
                      {(e.images || []).length > 0 && (
                        <div className="grid grid-cols-3 gap-1.5 pt-1">
                          {e.images.map((im, i) => (
                            <button key={i} onClick={() => setLightbox(fileUrl(im.fileId))} data-testid={`proposal-est-img-${e.id}-${i}`} className="aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200"><img src={fileUrl(im.fileId)} alt="" className="h-full w-full object-cover" loading="lazy" /></button>
                          ))}
                        </div>
                      )}
                      {e.layoutPlanRef?.fileId && (
                        <div className="pt-1" data-testid={`proposal-est-layout-${e.id}`}>
                          <p className="text-[11px] text-slate-400 mb-1">{T("布局方案", "Layout plan")} · {e.layoutPlanRef.name}</p>
                          <button onClick={() => setLightbox(fileUrl(e.layoutPlanRef.fileId))} className="w-full rounded-lg overflow-hidden bg-slate-100 border border-slate-200"><img src={fileUrl(e.layoutPlanRef.fileId)} alt={e.layoutPlanRef.name} className="w-full h-40 object-cover" loading="lazy" /></button>
                        </div>
                      )}
                      {e.pdfFileId && <a href={fileUrl(e.pdfFileId)} target="_blank" rel="noreferrer" data-testid={`proposal-est-pdf-${e.id}`} className="inline-flex items-center gap-1.5 text-[13px] text-indigo-600 hover:underline"><FileText className="h-4 w-4" />{T("报价 PDF", "Quote PDF")}</a>}
                      {!accepted && (
                        <button onClick={() => setAcceptFor(e)} data-testid={`proposal-accept-${e.id}`} className="mt-2 w-full rounded-xl bg-brand-500 hover:bg-brand-600 text-white py-2.5 font-semibold transition">{multi ? T("选择并接受此方案", "Choose & Accept") : T("接受此方案", "Accept")}</button>
                      )}
                      {isAccepted && <p className="text-center text-emerald-600 text-sm font-medium pt-1">{T("✓ 已接受此方案", "✓ Accepted")}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {(data.links || []).length > 0 && (
          <section data-testid="proposal-links">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{T("VR 漫游 / 视频", "VR & Video")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.links.map((lk, i) => (
                <a key={i} href={lk.url} target="_blank" rel="noreferrer" data-testid={`proposal-link-${i}`} className="flex items-center gap-3 rounded-xl bg-white border border-slate-200 p-4 hover:border-violet-300 hover:shadow-sm transition">
                  <PlayCircle className="h-8 w-8 text-violet-500 shrink-0" /><div className="min-w-0"><p className="text-sm font-medium text-slate-800 truncate">{lk.label || lk.url}</p><p className="text-[11px] text-slate-400 truncate">{lk.url}</p></div>
                </a>
              ))}
            </div>
          </section>
        )}

        {(data.images || []).length > 0 && (
          <section data-testid="proposal-images">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{T("效果图", "Renderings")}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {data.images.map((im, i) => (
                <button key={i} onClick={() => setLightbox(fileUrl(im.fileId))} data-testid={`proposal-image-${i}`} className="aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 border border-slate-200 hover:opacity-90 transition"><img src={fileUrl(im.fileId)} alt={im.filename} className="h-full w-full object-cover" loading="lazy" /></button>
              ))}
            </div>
          </section>
        )}

        {(data.files || []).length > 0 && (
          <section data-testid="proposal-files">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{T("方案文件", "Documents")}</h2>
            <div className="space-y-2">
              {data.files.map((f, i) => (
                <a key={i} href={fileUrl(f.fileId)} target="_blank" rel="noreferrer" data-testid={`proposal-file-${i}`} className="flex items-center justify-between rounded-xl bg-white border border-slate-200 px-4 py-3 hover:border-indigo-300">
                  <span className="flex items-center gap-2 text-sm text-slate-700 truncate"><FileText className="h-4 w-4 text-indigo-500 shrink-0" /><span className="truncate">{f.filename}</span></span><Download className="h-4 w-4 text-slate-400" />
                </a>
              ))}
            </div>
          </section>
        )}
      </main>

      <footer className="max-w-4xl mx-auto px-5 py-8 text-center text-[11px] text-slate-400">{data.companyName} · (626) 803-8000 / (408) 900-6688</footer>

      {acceptFor && <AcceptModal estimate={acceptFor} submitting={submitting} onClose={() => setAcceptFor(null)} onConfirm={confirmAccept} />}
      {bookOpen && <BookVisitModal clientName={data.clientName} submitting={booking} T={T} onClose={() => setBookOpen(false)} onConfirm={submitBooking} />}
      {booked && (
        <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4" data-testid="book-visit-success" onClick={() => setBooked(false)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-3"><Check className="h-6 w-6" /></div>
            <p className="font-bold text-slate-800 text-lg">{T("预约成功！", "Booked!")}</p>
            {bookedInfo && (bookedInfo.date || bookedInfo.timeSlot) && (
              <p className="text-sm text-slate-700 mt-2 bg-slate-50 rounded-lg py-2 px-3" data-testid="book-visit-when">
                {T("预计上门时段", "Preferred time")}：<b>{bookedInfo.date || ""} {bookedInfo.timeSlot || ""}</b>
              </p>
            )}
            <p className="text-sm text-slate-500 mt-2">{T("您的专属顾问会尽快与您联系确认现场勘测时间。", "Your advisor will contact you shortly to confirm the site visit.")}</p>
            <button data-testid="book-visit-success-close" onClick={() => setBooked(false)} className="mt-4 rounded-xl bg-slate-900 text-white px-5 py-2 text-sm">{T("好的", "OK")}</button>
          </div>
        </div>
      )}
      {compareOpen && (
        <div data-testid="proposal-compare-modal" className="fixed inset-0 z-[9999] bg-slate-950 flex flex-col">
          <div className="mobile-safe-topbar flex items-center justify-between px-4 py-3 bg-slate-900 text-white">
            <span className="font-semibold flex items-center gap-2"><Layers className="h-4 w-4 text-red-500" />{T("方案并排对比", "Compare Site Plans")}</span>
            <button data-testid="proposal-compare-close" onClick={() => setCompareOpen(false)} className="flex items-center gap-1 rounded-full bg-slate-800 hover:bg-slate-700 px-3 py-1.5 text-sm"><X className="h-4 w-4" />{T("关闭", "Close")}</button>
          </div>
          <div className="flex-1 overflow-auto p-3">
            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min((data.siteLayouts || []).length, 3)}, minmax(0, 1fr))` }}>
              {(data.siteLayouts || []).map((lay, i) => {
                const cc = (lay.zones || []).flatMap((z) => z.coords || []);
                const ct = (data.lat && data.lng && i === 0) ? [Number(data.lat), Number(data.lng)]
                  : cc.length ? [cc.reduce((s, c) => s + c[0], 0) / cc.length, cc.reduce((s, c) => s + c[1], 0) / cc.length] : null;
                const adu = (lay.zones || []).find((z) => z.kind === "adu");
                return (
                  <div key={lay.id || i} data-testid={`proposal-compare-card-${i}`} className="bg-white rounded-xl overflow-hidden flex flex-col">
                    <div className="px-3 py-2 bg-slate-100 flex items-center justify-between">
                      <span className="font-semibold text-slate-800 text-sm truncate">{lay.recommended && "★ "}{lay.name}</span>
                      {adu && <span className="text-[11px] text-red-600 font-medium shrink-0">{Number(adu.areaSqft).toLocaleString()} sf</span>}
                    </div>
                    <div style={{ height: 300 }}>
                      {ct && <LayoutMap key={`cmp-${i}`} center={ct} zones={lay.zones} annotations={lay.annotations} parcel={data.parcel} heading={lay.heading || 0} zoom={20} zh={zh} />}
                    </div>
                    {lay.note && <p className="text-[11px] text-slate-500 p-2 line-clamp-3">{lay.note}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="proposal-lightbox">
          <button className="mobile-safe-close absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setLightbox(null)}><X className="h-7 w-7" /></button>
          <img src={lightbox} alt="" className="max-w-full max-h-[90vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
      {annotPlan && (
        <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4" data-testid="proposal-annot-modal" onClick={() => setAnnotPlan(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <span className="font-semibold text-slate-800 text-sm flex items-center gap-1.5"><MapPin className="h-4 w-4 text-amber-500" />{T("在方案图上标注建议", "Mark a note on the plan")}</span>
              <button data-testid="proposal-annot-close" onClick={() => setAnnotPlan(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-[12px] text-slate-500">{T("点击图片选择位置，再写下您想调整的地方（例如：门开在这一侧 / 这里想留车位）。", "Tap the image to pick a spot, then tell us what you'd change.")}</p>
              <div className="relative w-full rounded-lg overflow-hidden border border-slate-200 cursor-crosshair" data-testid="proposal-annot-image"
                onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setAnnotPin({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height }); }}>
                <img src={fileUrl(annotPlan.fileId)} alt={annotPlan.name} className="w-full block" />
                {(annotPlan.ownerNotes || []).map((n, i) => (
                  <div key={n.id || i} className="absolute -translate-x-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-amber-500 text-white text-[11px] font-bold flex items-center justify-center border-2 border-white" style={{ left: `${n.x * 100}%`, top: `${n.y * 100}%` }} title={n.text}>{i + 1}</div>
                ))}
                {annotPin && <div className="absolute -translate-x-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-red-600 text-white flex items-center justify-center border-2 border-white animate-pulse" style={{ left: `${annotPin.x * 100}%`, top: `${annotPin.y * 100}%` }} data-testid="proposal-annot-pin"><MapPin className="h-3.5 w-3.5" /></div>}
              </div>
              <textarea data-testid="proposal-annot-text" value={annotText} onChange={(e) => setAnnotText(e.target.value)} rows={3} placeholder={T("写下您的建议…", "Type your note…")} className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 resize-none" />
              <button data-testid="proposal-annot-submit" onClick={submitAnnot} disabled={annotBusy || !annotText.trim()} className="w-full rounded-xl bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white py-2.5 font-semibold text-sm">
                {annotBusy ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : T("提交建议", "Send note")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
